import InputHandler from 'Components/InputHandler';
import * as React from 'react';
import { TDCSContext } from 'Routes/Tdcs/Document';
import { styled } from '@mui/system';
import colors from '../../../../../../../config/theme/colors';
import { Container, Item } from 'Components/Grid';
import { capitalize } from 'Utility/string';
import { useTabulationTools } from 'Utility/customHooks';
import { isPrimarySymptom } from 'Utility/ninmtUtil';
import { PrimaryLabel } from 'Components/_NewElements/Surveys';
import { getNestedKeys } from 'Utility/object';
import { path } from 'Utility/ramdaReplacement';

const InputContainer = styled('div')({
  margin: '0 0 2.5rem 0.9rem',
});

const InputHeaderContainer = styled(InputContainer)({
  color: colors.tertiaryText,
  marginBottom: '0rem',
});

const InputTitleContainer = styled(Item)({
  paddingLeft: '1.5rem',
  color: colors.tertiaryText,
});

const PatientsRating = ({
  document,
  onChange,
  editingEvent = true,
  currentTreatmentPeriodId,
}: IPatientsRating): React.JSX.Element => {
  const tdcsContext = React.useContext(TDCSContext);
  const { isEditing, fm, symptomsAndLocations } = tdcsContext;

  // onChange function for individual patient's rating values
  const onChangeArray = (value: TOnChangeValues): void => {
    const pathArr = getNestedKeys(value);
    if (pathArr.length !== 4) return;
    // path[0]: 'ratingByLocation'
    const newValues = structuredClone(document[pathArr[0] as keyof ITDCSRating] ?? []);
    const valueInput = path(pathArr, value);

    // path[1]: index
    const path1 = Number(pathArr[1]);
    const obj = newValues[path1];

    if (!obj || typeof obj !== 'object') return;
    if (typeof valueInput !== 'number' && valueInput !== null) return;

    // path[2]: 'intensity' or 'harm'
    const path2 = pathArr[2] as keyof typeof obj;
    const child = obj[path2];

    if (!child || typeof child !== 'object' || !('beforeTreatment' in child) || !('afterTreatment' in child)) {
      return;
    }

    // path[3]: 'beforeTreatment' or 'afterTreatment'
    const path3 = pathArr[3] as keyof typeof child;
    (child as { [key: string]: number | null })[path3] = valueInput;

    onChange({ [pathArr[0] as keyof ITDCSRating]: newValues });
  };

  type TName = 'intensity' | 'harm';
  type TPeriod = 'beforeTreatment' | 'afterTreatment';

  const { symptom, ratingByLocation } = document;
  const noLocation = symptom === 'other';

  const { currentId, inputRefs, onKeyDown } = useTabulationTools({
    currentIdStart: '10',
    childrenLength: ratingByLocation ? ratingByLocation.length * 2 + 1 : 0,
  });

  // Used to calculate row indexes through entire table
  let rowIndex = 0;

  const isPrimary = (locOrDesc: string | null | undefined): boolean => {
    return isPrimarySymptom(currentTreatmentPeriodId, symptomsAndLocations, symptom, locOrDesc);
  };

  return (
    <div onKeyDown={onKeyDown} style={{ padding: '2rem 0 0 0' }}>
      {ratingByLocation
        ?.map((rating, index) => ({ rating, index }))
        .toSorted(
          (a, b) =>
            Number(isPrimary(noLocation ? b.rating.description : b.rating.location)) -
            Number(isPrimary(noLocation ? a.rating.description : a.rating.location)),
        )
        .map(({ rating, index }: { rating: TTDCSRatingByLocation; index: number }, displayIndex: number) => {
          return (
            <React.Fragment key={index}>
              <Container>
                <Item xs={5} style={{ fontWeight: 600, display: 'flex', flexWrap: 'wrap' }}>
                  <InputContainer>
                    {noLocation
                      ? rating.description && rating.description.length > 0
                        ? rating.description
                        : '-'
                      : rating.location
                        ? fm(`tdcs.patientsRating.opts.${rating.location}`)
                        : '-'}
                    {isPrimary(noLocation ? rating.description : rating.location) && <PrimaryLabel fm={fm} />}
                  </InputContainer>
                </Item>
                <Item xs={3} style={{ visibility: displayIndex > 0 ? 'hidden' : undefined }}>
                  <InputHeaderContainer>{fm('tdcs.patientsRating.beforeTreatment')}</InputHeaderContainer>
                </Item>
                <Item xs={3} style={{ visibility: displayIndex > 0 ? 'hidden' : undefined }}>
                  <InputHeaderContainer>{fm('tdcs.patientsRating.afterTreatment')}</InputHeaderContainer>
                </Item>
              </Container>
              {['intensity', 'harm'].map((name, ind) => {
                if (rowIndex < ratingByLocation.length * 2) rowIndex++;
                return (
                  <Container key={ind}>
                    <InputTitleContainer xs={5}>
                      {fm(`tdcs.patientsRating.${symptom === 'other' ? 'symptom' : symptom}${capitalize(name)}`)}
                    </InputTitleContainer>
                    {['beforeTreatment', 'afterTreatment'].map((period, i) => {
                      return (
                        <Item xs={3} key={i}>
                          <InputContainer>
                            <InputHandler
                              type="NumberField"
                              name={`ratingByLocation.${index}.${name}.${period}`}
                              editing={isEditing && editingEvent}
                              formData={{
                                document: {
                                  ratingByLocation: Object.assign([], ratingByLocation, {
                                    [index]: {
                                      ...rating,
                                      [name]: {
                                        ...rating[name as TName],
                                        [period]: rating[name as TName]?.[period as TPeriod],
                                      },
                                    },
                                  }),
                                },
                                onChange: onChangeArray,
                              }}
                              min={0}
                              max={10}
                              precision={1}
                              enableTrailingZero
                              width={10}
                              placeholder="0.0-10.0"
                              disablePlaceholderFormatting
                              tabulationTools={{
                                tabIndex: `${rowIndex}${i}` === currentId ? 0 : -1,
                                inputRefs,
                                identifier: `${rowIndex}${i}`,
                              }}
                            />
                          </InputContainer>
                        </Item>
                      );
                    })}
                  </Container>
                );
              })}
            </React.Fragment>
          );
        })}
    </div>
  );
};

interface IPatientsRating {
  document: ITDCSRating;
  onChange: (value: TOnChangeValues) => void;
  editingEvent?: boolean;
  treatmentEfficiencyDocs?: Array<IDocumentWithTypeV2<'treatmentEfficiency'>>;
  currentTreatmentPeriodId?: string;
}

export default PatientsRating;
